import React, { useCallback } from 'react'
import tw, { css } from 'twin.macro'
import { graphql } from 'gatsby'
import { ContentfulRichTextGatsbyReference, RenderRichTextData } from 'gatsby-source-contentful/rich-text'
// eslint-disable-next-line import/no-cycle
import RichText from '@/components/ui/rich-text'
import Link from '@/components/link-component'
import ImageContentful from '@/components/ui/image-contentful'
import colors from '@/constants/colors'

export type QuoteProps = {
  contentful_id?: string
  attribution: string
  backgroundColor?: string
  body: RenderRichTextData<ContentfulRichTextGatsbyReference>
  citation: string
  image?: Queries.ContentfulAsset
  imageAspectRatio?: number
  layout?: 'horizontal' | 'vertical'
  alignment?: 'left' | 'center' | 'right'
  link?: Queries.ContentfulComponentLink
}

export const fragment = graphql`
  fragment Quote on ContentfulComponentQuote {
    __typename
    contentful_id
    attribution
    backgroundColor
    body {
      raw
      #  references {
      #    ...Link
      #  }
    }
    image {
      ...Asset
    }
    link {
      ...Link
    }
  }
`

export default function Quote({
  attribution,
  backgroundColor,
  body,
  citation = '',
  image,
  imageAspectRatio = 16 / 9,
  link,
  alignment = 'center',
  layout = 'vertical',
}: QuoteProps): JSX.Element {
  const LinkWrap = useCallback(
    ({ children, to }) => (
      <>
        {to ? (
          <Link tw="w-full" to={to}>
            {children}
          </Link>
        ) : (
          children
        )}
      </>
    ),
    []
  )
  const { indigoBlue, primaryBlue, lightGray, white } = colors

  return (
    <LinkWrap to={link && link.url}>
      <figure
        tw="bg-indigoBlue text-white p-8 mb-1 rounded-3xl"
        css={[
          (backgroundColor === indigoBlue || backgroundColor === null) && tw`bg-indigoBlue`,
          backgroundColor === primaryBlue && tw`bg-primaryBlue`,
          backgroundColor === lightGray && tw`bg-gray-100 text-indigoBlue`,
          backgroundColor === white && tw`bg-white text-indigoBlue`,
          layout === 'horizontal' &&
            tw`h-full flex flex-col sm:flex-row sm:items-center sm:justify-center p-[36px 38px 38px 39px] md:p-[60px 32px 60px 60px]`,
          alignment === 'left' && tw`text-left`,
          (!alignment || alignment === 'center') && tw`text-center`,
          alignment === 'right' && tw`text-right`,
        ]}
      >
        {image && (
          <div
            css={css([
              tw`flex items-center overflow-hidden [&*>svg]:m-auto mb-5 rounded-xl pointer-events-none`,
              layout === 'horizontal' ? tw`w-full h-[] sm:(w-5/12) sm:mr-10 mb-8 sm:mb-0` : tw`w-1/2 m-auto`,
              {
                aspectRatio: `${imageAspectRatio}` || '16 / 9',
              },
            ])}
          >
            <ImageContentful {...image} tw="w-full" />
          </div>
        )}
        <div
          tw="[&*]:text-indigoBlue flex-1 px-2 md:px-4"
          css={[
            layout === 'vertical' && image && tw`pt-8`,
            layout === 'horizontal' && tw`flex flex-col-reverse text-left sm:px-0 pt-0`,
            (backgroundColor === primaryBlue || backgroundColor === indigoBlue || backgroundColor === null) &&
              tw`[&*]:text-white`,
          ]}
        >
          <blockquote tw="text-base leading-normal font-normal inline-block [p:last-child]:mb-0 ">
            <RichText body={body} />
          </blockquote>
          <figcaption
            tw="font-bold leading-tight [cite]:not-italic [cite]:block"
            css={[
              layout === 'horizontal' &&
                tw`text-2xl sm:text-3xl font-normal leading-[1.2] sm:leading-[1.1] mb-2 sm:mb-7`,
            ]}
          >
            <cite tw="mt-4">{attribution}</cite>
            {citation && (
              <>
                {', '}
                <cite>{citation}</cite>
              </>
            )}
          </figcaption>
        </div>
      </figure>
    </LinkWrap>
  )
}
